import { frameRange } from "@pixelpuf/schemas/state";
import type { FrameRange, StateFrameRanges } from "@pixelpuf/schemas/state";

/**
 * Re-export the schema-package primitives so app code can keep importing
 * `#puf/machine.ts` for FrameRange / StateFrameRanges types and the
 * `frameRange()` constructor — the canonical implementations live in
 * `@pixelpuf/schemas/state` and this module is the editor-side shim.
 *
 * Animation-machine concepts (animMachine, AnimEvent, STATE_IDS, DEFAULT_STATE_FRAMES)
 * lived here pre-Phase-5; they're gone now. The runtime machine is built per-project
 * from the user's `stateMachine` config via `#puf/machines/build.ts`; starter values
 * live in `#puf/machines/starter.ts`.
 */
export { frameRange } from "@pixelpuf/schemas/state";
export type { StateFrameRanges, StateId } from "@pixelpuf/schemas/state";

/** Clamp a state-frame range against the current frame count so playback /
 * range editors never reference frames that don't exist. */
function clampRange(range: FrameRange, frameCount: number): FrameRange {
  const max = Math.max(0, frameCount - 1);
  const start = Math.min(range[0], max);
  const end = Math.min(range[1], max);
  return frameRange(start, Math.max(start, end));
}

/** Clamp every range in a `StateFrameRanges` map. Iterates the actual keys —
 * works for user-defined state ids. */
export function clampStateFrames(ranges: StateFrameRanges, frameCount: number): StateFrameRanges {
  const out: StateFrameRanges = {};
  for (const [id, range] of Object.entries(ranges)) {
    out[id] = clampRange(range, frameCount);
  }
  return out;
}
